import { parseDbTimestamp, pickT } from "@repo/shared";
import { isFlowInstanceEditable } from "@repo/shared/types";
import {
  ArrowLeft,
  ArrowRight,
  Ban,
  Check,
  Clock,
  Landmark,
  LockOpen,
  RotateCcw,
  ShieldCheck,
  Trophy,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { get } from "@/lib/api/_client";
import * as flowInstancesApi from "@/lib/api/flow-instances/flow-instances";
import { useMe } from "@/lib/hooks/use-clients";
import { useProcessState, useSignatures } from "@/lib/hooks/use-flow-instances/use-flow-instances";
import { useInvalidateFlowProcess } from "@/lib/hooks/use-flow-process-invalidation";
import { useStandaloneAutoContinue } from "@/lib/hooks/use-standalone-auto-continue";
import { useStandaloneMode } from "@/lib/hooks/use-standalone-mode";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { hasPermission, type Permission } from "@/lib/permissions";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { ReopenFlowDialog } from "../flow-wizard/reopen-flow-dialog";
import { PdfGenerationSection } from "../pdf-generation-section";
import { getProcessStepLabel, ProcessStepper } from "./process-stepper";
import { T } from "./process-view.i18n";
import { ProductDocumentsSection } from "./product-documents-section";
import { SignatureSection } from "./signature-section";
import { SigningStatusCard } from "./signing-status-card";
import { StandaloneReturnActions } from "./standalone-return-actions";

type ProcessViewProps = {
  flowInstanceId: string;
  flowId: string;
  flowName: string;
  clientId: string;
  clientEmail: string | null;
  clientMobilePhone: string | null;
};

type PendingConfirm =
  | "unlock"
  | "reopen"
  | "withdrawCompliance"
  | "withdrawExternal"
  | "approveCompliance"
  | "signingBack"
  | { kind: "deleteSignature"; orderId: string }
  | null;

export function ProcessView({
  flowInstanceId,
  flowId,
  flowName,
  clientId,
  clientEmail,
  clientMobilePhone,
}: ProcessViewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { userLanguage } = useUserLanguage();
  const { formatDateTime } = useRegionalFormat();
  const t = useT(T);
  // QL-33 / QL-261 — standalone launches carry the advisor back to the system
  // that opened them (the instance's persisted internalRedirectUrl, else the
  // link's param — sanitized to http(s)); the standalone params must survive
  // navigation back to the flow.
  const { internalRedirectUrl, autoContinueUrl, withStandaloneParams } = useStandaloneMode();

  const { processState, isLoading, mutate } = useProcessState(flowInstanceId);
  const { me } = useMe();
  const isComplianceUser = hasPermission(
    (me?.permissions || ["advisor"]) as Permission[],
    "compliance",
  );
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [draftOpen, setDraftOpen] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);
  // Signed documents must stay visible on completed instances even when the
  // flow's signing flags were turned off afterwards — the artifacts exist.
  const { signatures } = useSignatures(flowInstanceId);
  const activeSignatureOrder =
    signatures.find((o) => o.status === "pending" || o.status === "signing") ?? null;
  const hasSignedOrder = signatures.some((o) => o.status === "signed");
  const [pdfCount, setPdfCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [confirm, setConfirm] = useState<PendingConfirm>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const status = processState?.status;

  // QL-264 / QL-263 — when the process reaches a hand-off step (review or
  // completed — STANDALONE_RETURN_STATUSES) while a standalone-launched
  // advisor is watching (auto-advance finishing a moment after submit,
  // compliance approving in another tab, …), return them to the partner system
  // after a short countdown. The Continue button stays for an immediate click;
  // "Stay on this page" cancels. Arriving straight from the submit counts as
  // watching, so a process that advanced between the submit response and the
  // first read here still returns. The cancelled card shows the Continue
  // button only (QL-737): the wizard returns the advisor on cancel when the
  // target is persisted on the instance; this covers link-only launches, other
  // sessions and later visits.
  // Latched once: the navigation state is cleared right away (below) so a
  // reload or back/forward visit to a long-completed instance never re-arms.
  const [arrivedFromSubmit] = useState(
    () =>
      (location.state as { standaloneSubmitted?: boolean } | null)?.standaloneSubmitted === true,
  );
  useEffect(() => {
    if (!arrivedFromSubmit) return;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [arrivedFromSubmit, navigate, location.pathname, location.search]);
  const autoContinue = useStandaloneAutoContinue({
    status,
    returnUrl: internalRedirectUrl,
    // Only a return target persisted on the instance may fire without a click.
    autoReturn: autoContinueUrl !== null,
    armed: arrivedFromSubmit,
  });

  // PDF count feeds the signature section
  useEffect(() => {
    if (!status || isFlowInstanceEditable(status)) return;
    get<any[]>(`/internal/pdf-generation/logs/${flowInstanceId}`)
      .then((logs) => setPdfCount(logs.filter((l) => l.status === "completed").length))
      .catch(() => setPdfCount(0));
  }, [flowInstanceId, status, refreshKey]);

  /** Refresh everything that can change on a process transition. */
  const invalidateProcess = useInvalidateFlowProcess(flowInstanceId, clientId);
  const refresh = () => {
    mutate();
    invalidateProcess();
    setRefreshKey((prev) => prev + 1);
  };

  const runAction = async (action: () => Promise<unknown>, onSuccess?: () => void) => {
    setActionError(null);
    const statusWhenClicked = processState?.status;
    try {
      await action();
      refresh();
      onSuccess?.();
    } catch (err) {
      log.error({ err, flowInstanceId }, "process action failed");
      // If the instance moved on while this page was open (another user, the
      // external system, a signature callback), the raw guard message is
      // confusing — say what actually happened instead.
      let moved = false;
      try {
        const fresh = await flowInstancesApi.getProcessState(flowInstanceId);
        moved = !!statusWhenClicked && fresh.status !== statusWhenClicked;
      } catch {
        // fall through to the raw message
      }
      setActionError(moved ? t.processMoved : err instanceof Error ? err.message : t.error);
      refresh();
    }
  };

  /** Unlock/reopen mean "I want to edit" — land the user in the flow wizard. */
  const goToFlow = () =>
    navigate(withStandaloneParams(`/clients/${clientId}/flows/${flowInstanceId}`));

  if (isLoading || !processState) return null;

  const pendingCompliance = processState.approvals.find(
    (a) => a.stepType === "compliance" && a.status === "pending",
  );

  // QL-292 — one way back to editing from every step past `submitted`,
  // replacing the delete-PDF-then-unlock two-step. `submitted` keeps unlock.
  // QL-399 — same verb, same dialog and same eligibility as the flow header
  // for every unfinished step. A CANCELLED instance is still the exception:
  // its card below keeps its own "Reopen for editing" confirm and sends no
  // reason, because `reopenInstance` is a different, shorter path.
  const returnToDraftButton = (
    <Button variant="outline" onClick={() => setDraftOpen(true)}>
      <RotateCcw className="h-4 w-4 mr-2" />
      {t.returnToDraft}
    </Button>
  );

  type ConfirmConfig = {
    title: string;
    description: string;
    action: () => Promise<unknown>;
    onSuccess?: () => void;
    confirmText?: string;
    variant?: "default" | "destructive";
  };

  const resolveConfirm = (pending: Exclude<PendingConfirm, null>): ConfirmConfig => {
    if (typeof pending === "object") {
      return {
        title: t.deleteSignatureConfirmTitle,
        description: t.deleteSignatureConfirmDescription,
        // Every failing path on this endpoint answers non-2xx today (so the client
        // throws), but the payload also carries `success` — check it so a future
        // 200-with-error can't read as a silent success.
        action: async () => {
          const result = await flowInstancesApi.cancelSignatureOrder(pending.orderId);
          if (!result.success) throw new Error(result.error || t.error);
        },
        confirmText: t.delete,
        variant: "destructive",
      };
    }

    const configs: Record<Exclude<PendingConfirm, null | { kind: string }>, ConfirmConfig> = {
      unlock: {
        title: t.unlockConfirmTitle,
        description: t.unlockConfirmDescription,
        action: () => flowInstancesApi.processUnlock(flowInstanceId),
        onSuccess: goToFlow,
      },
      reopen: {
        title: t.reopenConfirmTitle,
        description: t.reopenConfirmDescription,
        action: () => flowInstancesApi.processReopen(flowInstanceId),
        onSuccess: goToFlow,
      },
      withdrawCompliance: {
        title: t.withdrawComplianceConfirmTitle,
        description: t.withdrawComplianceConfirmDescription,
        action: () => flowInstancesApi.complianceWithdraw(flowInstanceId),
      },
      withdrawExternal: {
        title: t.withdrawExternalConfirmTitle,
        description: t.withdrawExternalConfirmDescription,
        action: () => flowInstancesApi.externalWithdraw(flowInstanceId),
      },
      approveCompliance: {
        title: t.approveConfirmTitle,
        description: t.approveConfirmDescription,
        action: () => flowInstancesApi.complianceApprove(flowInstanceId),
      },
      signingBack: {
        title: t.signingBackConfirmTitle,
        description: t.signingBackConfirmDescription,
        action: () => flowInstancesApi.signingBack(flowInstanceId),
      },
    };

    return configs[pending];
  };

  const activeConfirm = confirm ? resolveConfirm(confirm) : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t.process}
        description={t.processDescription(flowName)}
        actions={
          // QL-275 — once the flow is past editing there was no way back to the
          // submitted form; the wizard renders read-only for locked instances.
          !!status &&
          !isFlowInstanceEditable(status) && (
            <Button variant="outline" onClick={goToFlow}>
              {t.reviewSubmission}
            </Button>
          )
        }
      />

      {!!status && isFlowInstanceEditable(status) ? (
        <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 flex items-center justify-between">
          <p className="text-sm text-warning">{t.notSubmitted}</p>
          <Button variant="outline" size="sm" onClick={goToFlow}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t.backToFlow}
          </Button>
        </div>
      ) : (
        <>
          <Card>
            <CardContent className="pt-6">
              <ProcessStepper steps={processState.steps} status={processState.status} />
            </CardContent>
          </Card>

          {actionError && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
              <p className="text-sm text-destructive">{actionError}</p>
            </div>
          )}

          {/* ── Cancelled: read-only + PDF + reopen ── */}
          {status === "cancelled" && (
            <>
              <Card className="border-destructive/40">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-destructive">
                    <Ban className="h-5 w-5" />
                    {t.cancelledTitle}
                  </CardTitle>
                  <CardDescription>{t.cancelledDescription}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button variant="outline" onClick={() => setConfirm("reopen")}>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    {t.reopen}
                  </Button>
                  {/* QL-737 — a standalone launch gets its way back to the
                      partner system here too (Continue only: `cancelled` is
                      not a STANDALONE_RETURN_STATUSES step, so the countdown
                      never runs on this card). */}
                  {internalRedirectUrl && (
                    <StandaloneReturnActions
                      returnUrl={internalRedirectUrl}
                      autoContinue={autoContinue}
                    />
                  )}
                </CardContent>
              </Card>
              <PdfGenerationSection
                flowInstanceId={flowInstanceId}
                flowId={flowId}
                onPdfChange={refresh}
                isLocked={false}
              />
            </>
          )}

          {/* ── Step: submitted ── */}
          {status === "submitted" && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>{t.submittedTitle}</CardTitle>
                  <CardDescription>{t.submittedDescription}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" onClick={() => setConfirm("unlock")}>
                    <LockOpen className="h-4 w-4 mr-2" />
                    {t.unlock}
                  </Button>
                </CardContent>
              </Card>
              <PdfGenerationSection
                flowInstanceId={flowInstanceId}
                flowId={flowId}
                onPdfChange={refresh}
                isLocked={false}
              />
            </>
          )}

          {/* ── Step: pdf_generated ── */}
          {status === "pdf_generated" && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>{t.pdfTitle}</CardTitle>
                  <CardDescription>{t.pdfDescription}</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-wrap items-center gap-2">
                  <Button
                    onClick={() => runAction(() => flowInstancesApi.processAdvance(flowInstanceId))}
                  >
                    {t.continue}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                  {returnToDraftButton}
                </CardContent>
              </Card>
              <PdfGenerationSection
                flowInstanceId={flowInstanceId}
                flowId={flowId}
                onPdfChange={refresh}
                isLocked={false}
              />
              <ProductDocumentsSection flowInstanceId={flowInstanceId} />
            </>
          )}

          {/* ── Step: compliance_review ── */}
          {status === "compliance_review" && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-primary" />
                    {t.complianceTitle}
                  </CardTitle>
                  <CardDescription>{t.complianceDescription}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {pendingCompliance && (
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      {t.requestedAt(formatDateTime(parseDbTimestamp(pendingCompliance.createdAt)))}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    {isComplianceUser && (
                      <>
                        <Button
                          variant="outline"
                          className="text-success hover:text-success"
                          onClick={() => setConfirm("approveCompliance")}
                        >
                          <Check className="h-4 w-4 mr-2" />
                          {t.approve}
                        </Button>
                        <Button
                          variant="outline"
                          className="text-destructive hover:text-destructive"
                          onClick={() => {
                            setRejectComment("");
                            setRejectOpen(true);
                          }}
                        >
                          <X className="h-4 w-4 mr-2" />
                          {t.reject}
                        </Button>
                      </>
                    )}
                    <Button variant="outline" onClick={() => setConfirm("withdrawCompliance")}>
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      {t.withdrawRequest}
                    </Button>
                    {returnToDraftButton}
                  </div>
                  {internalRedirectUrl && (
                    <StandaloneReturnActions
                      returnUrl={internalRedirectUrl}
                      autoContinue={autoContinue}
                    />
                  )}
                </CardContent>
              </Card>
              <PdfGenerationSection flowInstanceId={flowInstanceId} flowId={flowId} readOnly />
            </>
          )}

          {/* ── Step: external_review ── */}
          {status === "external_review" && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Landmark className="h-5 w-5 text-primary" />
                    {t.externalTitle}
                  </CardTitle>
                  <CardDescription>{t.externalDescription}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {processState.externalStatusInfo && (
                    <div className="rounded-md bg-muted p-3 text-sm">
                      <p className="font-medium">{t.externalLatestUpdate}</p>
                      <p className="text-muted-foreground mt-1">
                        {processState.externalStatusInfo}
                      </p>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <Button variant="outline" onClick={() => setConfirm("withdrawExternal")}>
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      {t.withdrawRequest}
                    </Button>
                    {returnToDraftButton}
                  </div>
                  {internalRedirectUrl && (
                    <StandaloneReturnActions
                      returnUrl={internalRedirectUrl}
                      autoContinue={autoContinue}
                    />
                  )}
                </CardContent>
              </Card>
              <PdfGenerationSection flowInstanceId={flowInstanceId} flowId={flowId} readOnly />
            </>
          )}

          {/* ── Step: signing ── */}
          {status === "signing" && (
            <>
              {/* Action box mirroring the submitted step, so the way back out of
                  signing is visible without scrolling past the sections below. */}
              {!hasSignedOrder && (
                <SigningStatusCard
                  activeOrder={activeSignatureOrder}
                  onRevert={() => setConfirm("signingBack")}
                  onDeleteRequest={(orderId) => setConfirm({ kind: "deleteSignature", orderId })}
                  extraAction={returnToDraftButton}
                />
              )}
              <SignatureSection
                flowInstanceId={flowInstanceId}
                pdfCount={pdfCount}
                refreshKey={refreshKey}
                clientId={clientId}
                clientEmail={clientEmail}
                clientMobilePhone={clientMobilePhone}
                onSignatureChange={refresh}
                enableEidSigning={processState.flags.enableEidSigning}
                enableDigitalSigning={processState.flags.enableDigitalSigning}
              />
              <PdfGenerationSection flowInstanceId={flowInstanceId} flowId={flowId} readOnly />
            </>
          )}

          {/* ── Terminal: completed ── */}
          {status === "completed" && (
            <>
              <Card className="border-success/40">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-success">
                    <Trophy className="h-5 w-5" />
                    {t.completedTitle}
                  </CardTitle>
                  <CardDescription>{t.completedDescription}</CardDescription>
                </CardHeader>
                {/* QL-33 — standalone launches can carry an internalRedirectUrl
                    back to the external system that opened the flow; offer it
                    once the process is finished. The value is sanitized
                    (http/https only) by useStandaloneMode. */}
                {internalRedirectUrl && (
                  <CardContent>
                    <StandaloneReturnActions
                      returnUrl={internalRedirectUrl}
                      autoContinue={autoContinue}
                    />
                  </CardContent>
                )}
              </Card>
              {(processState.flags.enableEidSigning ||
                processState.flags.enableDigitalSigning ||
                signatures.length > 0) && (
                <SignatureSection
                  flowInstanceId={flowInstanceId}
                  pdfCount={pdfCount}
                  refreshKey={refreshKey}
                  clientId={clientId}
                  clientEmail={clientEmail}
                  clientMobilePhone={clientMobilePhone}
                />
              )}
              <PdfGenerationSection
                flowInstanceId={flowInstanceId}
                flowId={flowId}
                onPdfChange={refresh}
                readOnly
              />
            </>
          )}

          {/* ── Activity log: every process action, newest first ──
              Always shown when there is any event — including on
              completed/cancelled instances, so the full audit trail is
              visible even after the process finished. */}
          {processState.events.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t.activityLog}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ul className="space-y-3">
                  {(showAllHistory ? processState.events : processState.events.slice(0, 8)).map(
                    (e) => {
                      const tone = eventTone(e.type);
                      return (
                        <li key={e.id} className="text-sm border-l-2 pl-3 border-border">
                          <span
                            className={
                              tone === "success"
                                ? "text-success font-medium"
                                : tone === "destructive"
                                  ? "text-destructive font-medium"
                                  : "font-medium"
                            }
                          >
                            {eventLabel(e.type, userLanguage, e.fromStatus)}
                          </span>
                          <span className="text-muted-foreground ml-2">
                            {e.actorName ? `${e.actorName} · ` : ""}
                            {formatDateTime(parseDbTimestamp(e.createdAt))}
                          </span>
                          {e.comment && <p className="mt-1 text-muted-foreground">{e.comment}</p>}
                        </li>
                      );
                    },
                  )}
                </ul>
                {processState.events.length > 8 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    onClick={() => setShowAllHistory((v) => !v)}
                  >
                    {showAllHistory
                      ? t.showLessHistory
                      : t.showAllHistory(processState.events.length)}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      <Dialog
        open={rejectOpen}
        onOpenChange={(open) => {
          if (!open) setRejectOpen(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.rejectTitle}</DialogTitle>
            <DialogDescription>{t.rejectDescription}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="process-reject-comment">{t.rejectCommentLabel}</Label>
            <Textarea
              id="process-reject-comment"
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              placeholder={t.rejectCommentPlaceholder}
              rows={4}
            />
          </div>
          <DialogFooter>
            <DialogCancel>{t.cancel}</DialogCancel>
            <Button
              variant="destructive"
              disabled={!rejectComment.trim()}
              onClick={() => {
                setRejectOpen(false);
                runAction(() =>
                  flowInstancesApi.complianceReject(flowInstanceId, rejectComment.trim()),
                );
              }}
            >
              {t.reject}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReopenFlowDialog
        open={draftOpen}
        onOpenChange={setDraftOpen}
        // The service's own account of what reopening removes, rather than a
        // second reckoning assembled here from separate queries.
        reopen={processState.reopen}
        onConfirm={(reason) => {
          setDraftOpen(false);
          runAction(() => flowInstancesApi.processDraft(flowInstanceId, reason), goToFlow);
        }}
      />

      {activeConfirm && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setConfirm(null);
          }}
          title={activeConfirm.title}
          description={activeConfirm.description}
          confirmText={activeConfirm.confirmText}
          cancelText={t.cancel}
          variant={activeConfirm.variant}
          onConfirm={() => {
            setConfirm(null);
            runAction(activeConfirm.action, activeConfirm.onSuccess);
          }}
        />
      )}
    </div>
  );
}

// ── Activity-log event labels & tone ──────────────────────────────────────
function eventLabel(type: string, lang: string, fromStatus?: string | null): string {
  const t = pickT(T, lang);
  const labels: Partial<Record<string, string>> = t.eventLabels;
  const label = labels[type];
  if (!label) return type.replaceAll("_", " ");
  // QL-292 — "Returned to draft from External approval": the step it left is
  // what tells the reader the process had got that far before.
  if (type === "drafted" && fromStatus) {
    return t.eventFrom(label, getProcessStepLabel(fromStatus, lang));
  }
  return label;
}

function eventTone(type: string): "success" | "destructive" | "neutral" {
  if (["compliance_approved", "external_approved", "signed", "completed"].includes(type)) {
    return "success";
  }
  if (["cancelled", "compliance_rejected", "external_rejected", "auto_pdf_failed"].includes(type)) {
    return "destructive";
  }
  return "neutral";
}
